import type { CustomFile } from '../../types';

import type { TypeResolvedFeatures } from '../types';
import type { TypeUploadFeature } from '../core/types';

import createChunkFeature from './createChunkFeature';
import createFilterFeature from './createFilterFeature';
import createRetryFeature, { type TypeRetryFeatureDeps } from './createRetryFeature';
import createStallFeature from './createStallFeature';

// ----------------------------------------------------------------------------

export type { TypeChunkFeatureDeps } from './createChunkFeature';
export type { TypeRetryFeatureDeps } from './createRetryFeature';

export { sliceBlob } from './createChunkFeature';

export { default as createChunkFeature } from './createChunkFeature';
export { default as createFilterFeature } from './createFilterFeature';
export { default as createRetryFeature } from './createRetryFeature';
export { default as createStallFeature } from './createStallFeature';

// ----------------------------------------------------------------------------

export type TypeFeatureDeps = TypeRetryFeatureDeps & {
    /*
     * The file these features are for. Absent in batch mode, where one request carries many files
     * and anything per-file — chunking — has nothing to attach to.
     */
    file?: CustomFile;
    fnPrepareFile?: (file: CustomFile) => Blob | Promise<Blob>;
};

// ----------------------------------------------------------------------------

/*
 * Builds the feature list for one run. Each factory returns null when its own block is off, so
 * adding a feature is one entry here and one file next to this one — nothing in the core changes,
 * and a run with every block disabled still works.
 *
 * The array is ordered innermost first, which is what `composeAttempt` nests on. Retry before chunk
 * is the ordering that matters: chunk ends up outside, so retry is what wraps each individual
 * slice. Swapped, a single failed slice would restart the whole file.
 */
function createFeatures(features: TypeResolvedFeatures, deps: TypeFeatureDeps): TypeUploadFeature[] {
    const plugins: (TypeUploadFeature | null)[] = [
        createFilterFeature(features),
        createStallFeature(features),
        createRetryFeature(features, deps),
        deps.file
            ? createChunkFeature(features, {
                  file: deps.file,
                  isAborted: deps.isAborted,
                  fnPrepareFile: deps.fnPrepareFile,
              })
            : null,
    ];

    return plugins.filter((plugin): plugin is TypeUploadFeature => plugin !== null);
}

// ----------------------------------------------------------------------------

export default createFeatures;
